// Tag-based "ignore" filter for the ingredient / recipe catalogues.
//
// The user can set per-user lists of tags to hide (see model/settings.js
// IGNORE_INGREDIENT_TAGS / IGNORE_RECIPE_TAGS). Any ingredient or recipe
// carrying a matching tag is hidden EVERYWHERE the app shows a browseable list,
// and excluded from menu generation + the meal picker — so it's as if the item
// doesn't exist.
//
// IMPORTANT: this is a display/selection filter only. Already-referenced items
// (in saved menus, archived menus, or stock) must still RESOLVE, so callers that
// build id→item maps for ref resolution keep the UNFILTERED list.
//
// Matching is case-insensitive and ignores surrounding whitespace, mirroring the
// tag semantics used elsewhere (see model/recipes.js hasTag).

/** Normalise a tag for comparison (lowercased, trimmed). */
function norm(tag) {
  return String(tag ?? '')
    .trim()
    .toLowerCase();
}

/**
 * True when `item` carries at least one tag on the `ignoreTags` list. An item
 * with no tags, or an empty ignore list, is never ignored.
 */
export function isIgnored(item, ignoreTags) {
  const ignore = (ignoreTags ?? []).map(norm).filter(Boolean);
  if (ignore.length === 0) return false;
  const set = new Set(ignore);
  return (item?.tags ?? []).some((t) => set.has(norm(t)));
}

/**
 * Return a new array with any item carrying an ignored tag removed. Pure; the
 * input array is not mutated. An empty/undefined ignore list returns the items
 * unchanged (same reference semantics as filter → a new array).
 */
export function filterByIgnoredTags(items, ignoreTags) {
  const ignore = (ignoreTags ?? []).map(norm).filter(Boolean);
  if (ignore.length === 0) return items ?? [];
  const set = new Set(ignore);
  return (items ?? []).filter((item) => !(item?.tags ?? []).some((t) => set.has(norm(t))));
}
